import { User } from '../types';
import { supabase } from '../lib/supabaseClient';
import { compressImage, COMPRESSION_PRESETS } from '../utils/imageCompression';

/* =========================
   Star-only Reviews (DB: public.reviews)
   ========================= */

export type Review = {
  id: string;
  vybe_id: string;       // event id
  reviewer_id: string;
  reviewee_id: string;
  stars: number;         // 1..5
  created_at: string;
  updated_at: string;
};

export type UserRatingAgg = {
  user_id: string;
  avg_stars: number | null;
  rating_count: number;
};

/**
 * Aggregated stars for a user from the view public.user_ratings
 */
export const getUserRatingById = async (
  userId: string
): Promise<{ rating: number | null; ratingCount: number }> => {
  const { data, error } = await supabase
    .from('user_ratings')
    .select('*')
    .eq('user_id', userId)
    .maybeSingle();

  if (error) {
    console.error('getUserRatingById error', error);
    return { rating: null, ratingCount: 0 };
  }
  return {
    rating: data?.avg_stars ?? null,
    ratingCount: data?.rating_count ?? 0,
  };
};

/**
 * Submit (or update) a rating for a user for a given event (Vybe).
 * Keeps your original signature; now writes to public.reviews.
 */
export const submitUserRating = async (
  reviewerId: string,
  reviewedUserId: string,
  eventId: string,
  rating: number
): Promise<boolean> => {
  try {
    if (rating < 1 || rating > 5) {
      throw new Error('Rating must be between 1 and 5');
    }

    // Optional: ensure caller is the actual logged-in user
    const { data: auth } = await supabase.auth.getUser();
    if (!auth.user?.id || auth.user.id !== reviewerId) {
      throw new Error('Not authenticated as this reviewer');
    }

    // Only allow if these two users are eligible to rate each other for this event
    const allowed = await canRateUser(reviewedUserId, eventId, reviewerId);
    if (!allowed) {
      throw new Error('You can only rate users you were matched with for this Vybe');
    }

    // Upsert into reviews (unique: vybe_id, reviewer_id, reviewee_id)
    const { error } = await supabase
      .from('reviews')
      .upsert(
        {
          vybe_id: eventId,
          reviewer_id: reviewerId,
          reviewee_id: reviewedUserId,
          stars: rating,
        },
        { onConflict: 'vybe_id,reviewer_id,reviewee_id' }
      );

    if (error) {
      console.error('Error submitting rating:', error);
      throw error;
    }

    return true;
  } catch (error) {
    console.error('Error in submitUserRating:', error);
    throw error;
  }
};

/**
 * Reviews given by a specific user (reads from public.reviews)
 */
export const getUserReviewsGiven = async (userId: string) => {
  try {
    const { data, error } = await supabase
      .from('reviews')
      .select('*')
      .eq('reviewer_id', userId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching reviews given:', error);
      return [];
    }

    return (data ?? []).map((review: any) => ({
      id: review.id,
      reviewerId: review.reviewer_id,
      reviewedUserId: review.reviewee_id,
      eventId: review.vybe_id,
      rating: review.stars,
      createdAt: review.created_at,
      updatedAt: review.updated_at,
    }));
  } catch (error) {
    console.error('Error in getUserReviewsGiven:', error);
    return [];
  }
};

/**
 * Can currentUserId rate userId for eventId?
 * Allowed if:
 *  - Organizer (poster) is rating an accepted applicant, OR
 *  - Accepted applicant is rating the organizer.
 * (No hard dependency on event status here; gate in UI if you only want post-event.)
 */
export const canRateUser = async (
  userId: string,      // the person to be rated
  eventId: string,
  currentUserId: string
): Promise<boolean> => {
  try {
    const { data: event, error: eventError } = await supabase
      .from('events')
      .select('poster_id')
      .eq('id', eventId)
      .maybeSingle();

    if (eventError || !event) {
      console.error('Error fetching event:', eventError);
      return false;
    }

    if (currentUserId === event.poster_id && userId !== event.poster_id) {
      // Organizer rating an accepted applicant
      const { data, error } = await supabase
        .from('event_applications')
        .select('id')
        .eq('event_id', eventId)
        .eq('applicant_id', userId)
        .eq('status', 'accepted')
        .maybeSingle();
      return !error && !!data;
    } else if (currentUserId !== event.poster_id && userId === event.poster_id) {
      // Accepted applicant rating the organizer
      const { data, error } = await supabase
        .from('event_applications')
        .select('id')
        .eq('event_id', eventId)
        .eq('applicant_id', currentUserId)
        .eq('status', 'accepted')
        .maybeSingle();
      return !error && !!data;
    }

    return false;
  } catch (error) {
    console.error('Error in canRateUser:', error);
    return false;
  }
};

/**
 * Has reviewerId already rated reviewedUserId for eventId?
 */
export const hasRatedUser = async (
  reviewerId: string,
  reviewedUserId: string,
  eventId: string
): Promise<boolean> => {
  try {
    const { data, error } = await supabase
      .from('reviews')
      .select('id')
      .eq('reviewer_id', reviewerId)
      .eq('reviewee_id', reviewedUserId)
      .eq('vybe_id', eventId)
      .maybeSingle();

    if (error) {
      console.error('Error checking if user has been rated:', error);
      return false;
    }

    return !!data;
  } catch (error) {
    console.error('Error in hasRatedUser:', error);
    return false;
  }
};

/* =========================
   Avatars
   ========================= */

/**
 * Converts HEIC/PNG to JPEG if necessary.
 * @param file The input File object.
 * @returns A Promise that resolves to a Blob in JPEG format.
 */
async function ensureJpeg(file: File): Promise<Blob> {
  console.debug('[avatar] ensureJpeg: checking file', { name: file.name, type: file.type });
  const isHeic = file.type === 'image/heic' || /\.heic$/i.test(file.name);

  // If it's already a JPEG, return as is
  if (file.type === 'image/jpeg' && !isHeic) {
    console.debug('[avatar] ensureJpeg: file is already JPEG, returning as is');
    return file;
  }

  // If it's HEIC, use heic2any (dynamically imported to reduce bundle size)
  if (isHeic) {
    console.debug('[avatar] ensureJpeg: converting HEIC to JPEG using heic2any');
    try {
      const heic2any = (await import('heic2any')).default;
      const out = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.9 });
      if (Array.isArray(out)) {
        if (out.length > 0) {
          console.debug('[avatar] ensureJpeg: HEIC conversion successful (array)', { type: out[0].type, size: out[0].size });
          return out[0];
        } else {
          throw new Error('heic2any returned an empty array.');
        }
      }
      console.debug('[avatar] ensureJpeg: HEIC conversion successful (single blob)', { type: out.type, size: out.size });
      return out as Blob;
    } catch (e) {
      console.error('[avatar] ensureJpeg: HEIC conversion failed with heic2any', e);
      throw new Error(`HEIC conversion failed: ${e instanceof Error ? e.message : String(e)}`);
    }
  }

  // If it's PNG, convert to JPEG using canvas
  if (file.type === 'image/png') {
    console.debug('[avatar] ensureJpeg: converting PNG to JPEG via canvas');
    try {
      const bitmap = await createImageBitmap(file);
      const canvas = document.createElement('canvas');
      canvas.width = bitmap.width;
      canvas.height = bitmap.height;
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        throw new Error('Could not get 2D rendering context for canvas.');
      }

      ctx.drawImage(bitmap, 0, 0);
      const blob = await new Promise<Blob | null>((res) => canvas.toBlob(b => res(b), 'image/jpeg', 0.9));

      if (!blob) {
        throw new Error('Failed to convert PNG to JPEG Blob.');
      }
      console.debug('[avatar] ensureJpeg: PNG conversion successful', { type: blob.type, size: blob.size });
      return blob;
    } catch (e) {
      console.error('[avatar] ensureJpeg: PNG conversion failed with canvas', e);
      throw new Error(`PNG conversion failed: ${e instanceof Error ? e.message : String(e)}`);
    }
  }

  // For other image types, throw an error
  console.error('[avatar] ensureJpeg: Unsupported file type for conversion', file.type);
  throw new Error(`Unsupported image format: ${file.type}. Only JPG, PNG, and HEIC are supported.`);
}

/**
 * Uploads a profile image to Supabase Storage and returns the public URL
 * Now with compression to reduce egress costs
 */
export const uploadAvatarToStorage = async (file: File): Promise<string | null> => {
  try {
    const { data: { user }, error: getUserError } = await supabase.auth.getUser();
    if (getUserError || !user) {
      console.error('[avatar] user not authenticated for upload', getUserError);
      throw new Error('Not authenticated');
    }
    console.debug('[avatar] user', { uid: user?.id });

    // Convert to JPEG first
    const jpegBlob = await ensureJpeg(file);
    console.debug('[avatar] blob after ensureJpeg', { type: jpegBlob.type, size: jpegBlob.size });

    // Compress to reduce storage and egress
    const compressedBlob = await compressImage(jpegBlob, COMPRESSION_PRESETS.avatar);
    console.debug('[avatar] blob after compression', { type: compressedBlob.type, size: compressedBlob.size });

    const filename = `avatar_${Date.now()}.jpg`;
    const path = `${user.id}/${filename}`;
    console.debug('[avatar] upload path', path);

    const { data, error } = await supabase.storage
      .from('avatars')
      .upload(path, compressedBlob, {
        upsert: true,
        contentType: 'image/jpeg',
        cacheControl: '3600' // Cache for 1 hour
      });

    if (error) {
      console.error('[avatar] upload error', error);
      throw error;
    }
    console.debug('[avatar] uploaded', { path, data });

    const { data: pub } = supabase.storage.from('avatars').getPublicUrl(path);
    if (!pub?.publicUrl) {
      console.error('[avatar] publicUrl not found', pub);
      throw new Error('Public URL not generated');
    }
    console.debug('[avatar] publicUrl', pub.publicUrl);

    return pub.publicUrl;
  } catch (error) {
    console.error('[avatar] uploadAvatarToStorage failed:', error);
    throw error;
  }
};

// Alias for backward compatibility
export const uploadProfileImageToStorage = uploadAvatarToStorage;

/* =========================
   Connections / Profiles
   ========================= */

export const getConnectedUsers = async (userId: string): Promise<User[]> => {
  try {
    const { data: userEvents } = await supabase
      .from('events')
      .select('id')
      .eq('poster_id', userId);
    const userEventIds = (userEvents ?? []).map((event) => event.id);

    const { data: acceptedApplicants } = await supabase
      .from('event_applications')
      .select('applicant_id')
      .in('event_id', userEventIds.length ? userEventIds : ['00000000-0000-0000-0000-000000000000'])
      .eq('status', 'accepted');

    const { data: acceptedApplications } = await supabase
      .from('event_applications')
      .select('event_id')
      .eq('applicant_id', userId)
      .eq('status', 'accepted');

    const acceptedEventIds = (acceptedApplications ?? []).map((app) => app.event_id);

    const { data: eventPosters } = await supabase
      .from('events')
      .select('poster_id')
      .in('id', acceptedEventIds.length ? acceptedEventIds : ['00000000-0000-0000-0000-000000000000']);

    const connectedUserIds = [
      ...(acceptedApplicants ?? []).map((app) => app.applicant_id),
      ...(eventPosters ?? []).map((event) => event.poster_id),
    ].filter((id) => id !== userId);

    const uniqueConnectedUserIds = [...new Set(connectedUserIds)];
    if (!uniqueConnectedUserIds.length) return [];

    const { data: connectedUsers } = await supabase
      .from('profiles')
      .select('*')
      .in('id', uniqueConnectedUserIds);

    return (connectedUsers ?? []).map((user: any) => ({
      id: user.id,
      username: user.username,
      email: user.email,
      fullName: user.full_name,
      bio: user.bio,
      interests: user.interests,
      location: user.location,
      latitude: user.latitude,
      longitude: user.longitude,
      gender: user.gender,
      rating: user.rating,            // profiles may still have cached values
      ratingCount: user.rating_count, // (detail view refreshes from user_ratings)
      dateOfBirth: user.date_of_birth,
      isVerified: user.is_verified,
      verificationStatus: user.verification_status,
      subscriptionTier: user.subscription_tier,
      phone: user.mobile,
      avatarUrl: user.avatar_url,  // avatar_url is the only image field
      createdAt: user.created_at,
    }));
  } catch (error) {
    console.error('Error in getConnectedUsers:', error);
    return [];
  }
};

export const getUserById = async (id: string): Promise<User | null> => {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select(`
        id,
        username,
        full_name,
        email,
        bio,
        is_verified,
        rating,
        rating_count,
        avatar_url,
        interests,
        location,
        latitude,
        longitude,
        gender,
        date_of_birth,
        verification_status,
        subscription_tier,
        subscription_expires_at,
        mobile,
        created_at,
        profile_visibility,
        vybe_check_status,
        vybe_check_score,
        vybe_check_passed_at,
        positive_rating_count,
        total_rating_count,
        email_verified
      `)
      .eq('id', id)
      .maybeSingle();

    if (error || !data) return null;

    // Pull fresh aggregate rating from view
    const agg = await getUserRatingById(id);

    return {
      id: data.id,
      username: data.username,
      email: data.email,
      fullName: data.full_name,
      bio: data.bio,
      interests: data.interests,
      location: data.location,
      latitude: data.latitude,
      longitude: data.longitude,
      gender: data.gender,
      rating: agg.rating ?? data.rating ?? null,
      ratingCount: agg.ratingCount ?? data.rating_count ?? 0,
      dateOfBirth: data.date_of_birth,
      isVerified: data.is_verified,
      verificationStatus: data.verification_status,
      subscriptionTier: data.subscription_tier,
      subscriptionExpiresAt: data.subscription_expires_at,
      phone: data.mobile,
      avatarUrl: data.avatar_url,
      profileVisibility: data.profile_visibility,
      createdAt: data.created_at,
      vybeCheckStatus: data.vybe_check_status,
      vybeCheckScore: data.vybe_check_score,
      vybeCheckPassedAt: data.vybe_check_passed_at,
      positiveRatingCount: data.positive_rating_count,
      totalRatingCount: data.total_rating_count,
      email_verified: data.email_verified,
    };
  } catch (error) {
    console.error('Error in getUserById:', error);
    return null;
  }
};

export const getAllUsers = async (): Promise<User[]> => {
  try {
    const { data, error } = await supabase.from('profiles').select('*');
    if (error || !data) return [];

    return data.map((user: any) => ({
      id: user.id,
      username: user.username,
      email: user.email,
      fullName: user.full_name,
      bio: user.bio,
      interests: user.interests,
      location: user.location,
      latitude: user.latitude,
      longitude: user.longitude,
      gender: user.gender,
      rating: user.rating,
      ratingCount: user.rating_count,
      dateOfBirth: user.date_of_birth,
      isVerified: user.is_verified,
      verificationStatus: user.verification_status,
      subscriptionTier: user.subscription_tier,
      phone: user.mobile,
      avatarUrl: user.avatar_url,  // 👈 avatar_url
      createdAt: user.created_at,
    }));
  } catch (error) {
    console.error('Error in getAllUsers:', error);
    return [];
  }
};

/* =========================
   Notifications (with metadata)
   ========================= */

export const createNotification = async (
  userId: string,
  type: string,
  content: string,
  relatedId?: string,
  metadata?: Record<string, any>
): Promise<boolean> => {
  try {
    const { error } = await supabase.from('notifications').insert({
      user_id: userId,
      type,
      content,           // plain text only
      read: false,
      related_id: relatedId ?? null,
      metadata: metadata ?? null,
      created_at: new Date().toISOString(),
    });
    if (error) throw error;
    return true;
  } catch (error) {
    console.error('Error in createNotification:', error);
    return false;
  }
};

/* =========================
   Misc helpers
   ========================= */

export const getUsersWithSharedEvents = async (userId: string): Promise<User[]> => {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .neq('id', userId);

    if (error || !data) return [];

    return data.map((user: any) => ({
      id: user.id,
      username: user.username,
      email: user.email,
      fullName: user.full_name,
      bio: user.bio,
      interests: user.interests,
      location: user.location,
      latitude: user.latitude,
      longitude: user.longitude,
      gender: user.gender,
      dateOfBirth: user.date_of_birth,
      isVerified: user.is_verified,
      verificationStatus: user.verification_status,
      subscriptionTier: user.subscription_tier,
      phone: user.mobile,
      avatarUrl: user.avatar_url,
      createdAt: user.created_at,
    }));
  } catch (error) {
    console.error('Error in getUsersWithSharedEvents:', error);
    return [];
  }
};

/**
 * Are two users "connected" through accepted applications (either direction)?
 */
export const checkIfCanConnect = async (userId: string, currentUserId: string): Promise<boolean> => {
  try {
    const { data: userEvents } = await supabase
      .from('events')
      .select('id')
      .eq('poster_id', currentUserId);
    const userEventIds = (userEvents ?? []).map((e) => e.id);

    if (userEventIds.length > 0) {
      const { data: acceptedApplications } = await supabase
        .from('event_applications')
        .select('id')
        .in('event_id', userEventIds)
        .eq('applicant_id', userId)
        .eq('status', 'accepted')
        .limit(1);

      if ((acceptedApplications ?? []).length > 0) return true;
    }

    const { data: otherUserEvents } = await supabase
      .from('events')
      .select('id')
      .eq('poster_id', userId);
    const otherUserEventIds = (otherUserEvents ?? []).map((e) => e.id);

    if (otherUserEventIds.length > 0) {
      const { data: currentUserApplications } = await supabase
        .from('event_applications')
        .select('id')
        .in('event_id', otherUserEventIds)
        .eq('applicant_id', currentUserId)
        .eq('status', 'accepted')
        .limit(1);

      if ((currentUserApplications ?? []).length > 0) return true;
    }

    return false;
  } catch (error) {
    console.error('Error checking connection:', error);
    return false;
  }
};

export const getUserConversations = async (_userId: string): Promise<any[]> => {
  // Not implemented in this service
  return [];
};

/* =========================
   Favorites
   ========================= */

export const toggleFavorite = async (userId: string, eventId: string): Promise<boolean> => {
  try {
    const { data: profile, error: fetchError } = await supabase
      .from('profiles')
      .select('favorites')
      .eq('id', userId)
      .maybeSingle();

    if (fetchError) {
      console.error('Error fetching favorites:', fetchError);
      return false;
    }

    const currentFavorites = profile?.favorites || [];
    const isFavorited = currentFavorites.includes(eventId);

    let newFavorites: string[];
    if (isFavorited) {
      newFavorites = currentFavorites.filter((id: string) => id !== eventId);
    } else {
      newFavorites = [...currentFavorites, eventId];
    }

    const { error: updateError } = await supabase
      .from('profiles')
      .update({ favorites: newFavorites })
      .eq('id', userId);

    if (updateError) {
      console.error('Error updating favorites:', updateError);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error in toggleFavorite:', error);
    return false;
  }
};

/* =========================
   Subscriptions (unchanged)
   ========================= */

export const subscriptionPlans = [
  {
    id: 'subscribed-monthly',
    name: 'Subscribed Monthly',
    price: 5.99,
    features: [
      'Create and post events',
      'Apply to events',
      'Private messaging',
      'Advanced search features',
    ],
    tier: 'subscribed',
    durationMonths: 1,
    trialDays: 7,
  },
  {
    id: 'subscribed-yearly',
    name: 'Subscribed Annual',
    price: 59.99,
    features: [
      'All Subscribed Monthly features',
      'Save 17% compared to monthly',
      'Priority support',
      'Early access to new features',
    ],
    tier: 'subscribed',
    durationMonths: 12,
  },
];